import { useCallback, useEffect, useRef, useState } from "react";
import { useLocale } from "../i18n/useLocale.js";

const EXPORT_ITEMS = [
  { id: "pdf", labelKey: "web.export.openWerkplan", hintKey: "web.export.openWerkplanHint", requiresPlan: true },
  { id: "material", labelKey: "web.export.materialSummary", hintKey: "web.export.materialSummaryHint", requiresPlan: true },
  { id: "csv", labelKey: "web.export.cutListCsv", hintKey: "web.export.cutListCsvHint", requiresPlan: true },
];

const BACKUP_ITEM = {
  id: "backup",
  labelKey: "web.export.projectBackup",
  hintKey: "web.export.projectBackupHint",
  secondary: true,
};

const HANDOFF_ITEM = {
  id: "handoff",
  labelKey: "web.export.workshopHandoff",
  hintKey: "web.export.workshopHandoffHint",
  secondary: true,
};

export function ExportMenu({
  canExport = false,
  enableMobileHandoff = false,
  onPrintPdf,
  onMaterialSummary,
  onCutListCsv,
  onProjectBackup,
  onWorkshopHandoff,
  onOpenChange,
}) {
  const { display } = useLocale();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const panelId = "export-menu-panel";

  const closeMenu = useCallback((returnFocus = false) => {
    setOpen(false);
    onOpenChange?.(false);
    if (returnFocus) {
      triggerRef.current?.focus();
    }
  }, [onOpenChange]);

  const setMenuOpen = useCallback((next) => {
    if (next) {
      setOpen(true);
      onOpenChange?.(true);
      return;
    }

    closeMenu(true);
  }, [closeMenu, onOpenChange]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        closeMenu(true);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeMenu(true);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, closeMenu]);

  const run = (handler) => {
    closeMenu(true);
    handler();
  };

  const handlers = {
    pdf: onPrintPdf,
    material: onMaterialSummary,
    csv: onCutListCsv,
    backup: onProjectBackup,
    handoff: onWorkshopHandoff,
  };

  return (
    <div className="header-menu" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`header-menu-trigger export-menu-trigger${canExport ? " primary-button" : " ghost-button"}`}
        aria-controls={panelId}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setMenuOpen(!open)}
      >
        {display.t("web.actions.export")}
        <span className="header-menu-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="header-menu-panel" id={panelId}>
          {!canExport && (
            <p className="header-menu-note">{display.t("web.export.emptyPlanNote")}</p>
          )}
          {EXPORT_ITEMS.map((item) => {
            const disabled = item.requiresPlan && !canExport;
            return (
              <button
                key={item.id}
                type="button"
                className={`header-menu-item${disabled ? " header-menu-item--disabled" : ""}`}
                disabled={disabled}
                onClick={() => {
                  if (!disabled) run(handlers[item.id]);
                }}
              >
                <span className="header-menu-item-label">{display.t(item.labelKey)}</span>
                <span className="header-menu-item-hint">{display.t(item.hintKey)}</span>
              </button>
            );
          })}
          {enableMobileHandoff ? (
            <button
              type="button"
              className={`header-menu-item header-menu-item--secondary${!canExport ? " header-menu-item--disabled" : ""}`}
              disabled={!canExport}
              onClick={() => {
                if (canExport) run(handlers.handoff);
              }}
            >
              <span className="header-menu-item-label">{display.t(HANDOFF_ITEM.labelKey)}</span>
              <span className="header-menu-item-hint">{display.t(HANDOFF_ITEM.hintKey)}</span>
            </button>
          ) : null}
          <button
            type="button"
            className="header-menu-item header-menu-item--secondary"
            onClick={() => run(handlers.backup)}
          >
            <span className="header-menu-item-label">{display.t(BACKUP_ITEM.labelKey)}</span>
            <span className="header-menu-item-hint">{display.t(BACKUP_ITEM.hintKey)}</span>
          </button>
        </div>
      )}
    </div>
  );
}
